// src/modules/app-views/crm/pages/Boards/modals/CrmParametersModal/VoipGotoTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useCallback } from 'react';
import { Select, Alert, Spin, Descriptions, Button, Tag } from 'antd';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import { API } from 'constants/Api';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const VoipGotoTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connected, setConnected] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [lines, setLines] = useState([]);
  const [selectedLineId, setSelectedLineId] = useState(null);
  const [currentMapping, setCurrentMapping] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [data] = await axiosService.get({ url: API.GOTO.CONNECTION_STATUS, returnAsObject: true });

      if (data?.connected) {
        setConnected(true);
        // Backend: { connected, data: { email, accountKey, lines, ... }, userLineMapping }
        setConnectionInfo(data.data || null);
        setLines(data.data?.lines || []);

        // Check if user already has a line mapping
        const mapping = data.userLineMapping || null;
        setCurrentMapping(mapping);
        setSelectedLineId(mapping?.lineId || null);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSelectLine = async lineId => {
    setSaving(true);
    try {
      const [data] = await axiosService.put({
        url: API.GOTO.USER_LINE_MAPPING,
        data: { lineId },
      });
      setSelectedLineId(lineId);
      setCurrentMapping(data || { lineId });
      appService.message({ flag: 's', text: 'saved_successfully' });
    } catch {
      appService.message({ flag: 'e', text: 'error_saving_data' });
    } finally {
      setSaving(false);
    }
  };

  const handleClearMapping = async () => {
    setSaving(true);
    try {
      await axiosService.delete({ url: API.GOTO.USER_LINE_MAPPING });
      setSelectedLineId(null);
      setCurrentMapping(null);
      appService.message({ flag: 's', text: 'saved_successfully' });
    } catch {
      appService.message({ flag: 'e', text: 'error_saving_data' });
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const lineOptions = lines.map(line => ({
    value: line.id,
    label: buildLineLabel(line),
  }));

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-3" style={{ textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  // GoTo not connected at subscription level
  if (!connected) {
    return (
      <div className="p-3">
        <Alert
          type="warning"
          showIcon
          title={translateX('goto_not_connected')}
          description={translateX('goto_not_connected_description')}
        />
      </div>
    );
  }

  return (
    <div className="p-3">
      <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
        {connectionInfo?.email && (
          <Descriptions.Item label={translateX('goto_email')}>
            {connectionInfo.email}
          </Descriptions.Item>
        )}
        {connectionInfo?.accountKey && (
          <Descriptions.Item label={translateX('goto_account')}>
            {connectionInfo.accountKey}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Status">
          <Tag color="success">{translateX('goto_connected')}</Tag>
        </Descriptions.Item>
      </Descriptions>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          {translateX('goto_select_line')}
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <Select
            style={{ flex: 1 }}
            placeholder={translateX('goto_select_line')}
            options={lineOptions}
            value={selectedLineId}
            onChange={handleSelectLine}
            loading={saving}
            allowClear={false}
            showSearch
            optionFilterProp="label"
          />
          {selectedLineId && (
            <Button onClick={handleClearMapping} loading={saving}>
              {translateX('clear')}
            </Button>
          )}
        </div>
      </div>

      {currentMapping && (
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label={translateX('goto_user_line')}>
            {buildLineLabel(lines.find(l => l.id === selectedLineId)) || selectedLineId}
          </Descriptions.Item>
        </Descriptions>
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default VoipGotoTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildLineLabel(line) {
  if (!line) return '';
  // Backend fields: { id, name, number, userEmail }
  const parts = [];
  if (line.number) parts.push(line.number);
  if (line.name) parts.push(line.name);
  return parts.join(' — ') || line.id || '';
}
